import { Device } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EnergyReportGrid } from "./components/EnergyReportGrid";
import { QualitySummary } from "./components/QualitySummary";
import { CONSUMPTION_HIDDEN_COLUMNS, useConsumptionColumns } from "./hooks/useConsumptionColumns";
import { useEnergyReport } from "./hooks/useEnergyReport";
import { useEventAddresses } from "./hooks/useEventAddresses";
import { useChargeEventColumns, useFuelUpEventColumns } from "./hooks/useEventColumns";
import { useReportDateRange } from "./hooks/useReportDateRange";
import { mergeChargeSessions } from "./models/chargeEventRow";
import { countByStatus } from "./models/countByStatus";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { DateRangeSelect } from "@/components/input/DateRangeSelect";
import { useDecodeVins } from "@/hooks/devices/useDecodeVins";
import { useDevicesBySearchWithNumDevices } from "@/hooks/devices/useDevicesBySearch";
import getDeviceLicensePlate from "@/hooks/getDeviceLicensePlate";
import { useDriverNames } from "@/hooks/users/useDrivers";
import { formatDateTime } from "@/utilities/formatDateTime";
import { WorkbookRow } from "@/utilities/xlsx";

type TabKey = "consumption" | "chargeEvents" | "fuelUpEvents";

const DEVICE_LIMIT = 50_000;

const deviceFilterOptions = createFilterOptions<Device>({
  stringify: device =>
    `${device.name ?? ""} ${getDeviceLicensePlate(device)} ${device.serialNumber ?? ""}`,
});

export default function EnergyConsumptionReportPage() {
  const { t, i18n } = useTranslation();
  const { database, username, me } = useGeotabClient();
  const { fromDate, toDate, setFromDate, setToDate } = useReportDateRange();

  const [selectedDevices, setSelectedDevices] = useState<Device[]>([]);
  const [activeTab, setActiveTab] = useState<TabKey>("consumption");
  // On by default, matching the report built into Geotab, which presents a plug-in session as one
  // row. The ungrouped list stays one click away because the grouping has to be inferred.
  const [mergeSessions, setMergeSessions] = useState(true);

  const allDevicesQuery = useDevicesBySearchWithNumDevices("", false, DEVICE_LIMIT);
  const allDevices = useMemo(() => allDevicesQuery.data?.devices ?? [], [allDevicesQuery.data]);

  const devices = useMemo(
    () =>
      (selectedDevices.length > 0 ? selectedDevices : allDevices).map(device => ({
        id: device.id,
        name: device.name ?? "",
        licensePlate: getDeviceLicensePlate(device),
      })),
    [allDevices, selectedDevices],
  );

  const report = useEnergyReport(devices, fromDate, toDate);

  const vinDecodedByDeviceId = useVinDecoded(
    selectedDevices.length > 0 ? selectedDevices : allDevices,
  );

  // Grouping is a bucket-and-sort over every charge event in the fleet, and the row list gets a
  // new identity each time a vehicle arrives — so left ungated it would re-sort the whole fleet
  // hundreds of times over, including while a tab that never shows the result is open.
  const showChargeEvents = activeTab === "chargeEvents";
  const chargeEventRows = useMemo(
    () =>
      showChargeEvents && mergeSessions
        ? mergeChargeSessions(report.chargeEventRows)
        : report.chargeEventRows,
    [report.chargeEventRows, mergeSessions, showChargeEvents],
  );

  // Only for the view being looked at: a fleet-wide period holds thousands of positions, and
  // reverse geocoding the other two views costs the same call budget the figures need.
  const chargeAddresses = useEventAddresses(chargeEventRows, showChargeEvents);
  const fuelUpAddresses = useEventAddresses(report.fuelUpEventRows, activeTab === "fuelUpEvents");

  // Trips and fuel transactions reference a driver by id and nothing else, so the names come
  // from the driver list rather than off the entity.
  const driverNames = useDriverNames();

  const consumptionColumns = useConsumptionColumns(vinDecodedByDeviceId);
  const chargeEventColumns = useChargeEventColumns({
    vinDecodedByDeviceId,
    driverNames,
    addresses: chargeAddresses,
  });
  const fuelUpEventColumns = useFuelUpEventColumns({
    vinDecodedByDeviceId,
    driverNames,
    addresses: fuelUpAddresses,
  });

  const counts = useMemo(() => countByStatus(report.consumptionRows), [report.consumptionRows]);

  const periodLabel = `${formatDateTime(fromDate, i18n.language)} – ${formatDateTime(toDate, i18n.language)}`;
  const metadata = useMemo<WorkbookRow[]>(
    () => [
      [t("energyReport.export.database", "Datenbank"), database ?? ""],
      [t("energyReport.export.period", "Abfragezeitraum"), periodLabel],
      [
        t("energyReport.export.exportedAt", "Abfragezeitpunkt"),
        formatDateTime(new Date(), i18n.language),
      ],
      [t("energyReport.export.exportedBy", "Abfragender"), userLabel(me, username)],
      [
        t("energyReport.export.completeness", "Fahrzeuge vollständig / unvollständig / ohne Werte"),
        `${counts.ok} / ${counts.partial} / ${counts.missing + counts.failed}`,
      ],
    ],
    [t, database, periodLabel, i18n.language, me, username, counts],
  );

  return (
    <Stack spacing={2} sx={{ p: { xs: 1, md: 2 }, height: "100%" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
        <Autocomplete
          multiple
          size="small"
          options={allDevices}
          value={selectedDevices}
          onChange={(_event, value) => setSelectedDevices(value)}
          filterOptions={deviceFilterOptions}
          disableCloseOnSelect
          limitTags={2}
          getOptionLabel={device => {
            const plate = getDeviceLicensePlate(device);
            return plate ? `${device.name ?? ""} (${plate})` : (device.name ?? "");
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          sx={{ minWidth: { sm: 320 } }}
          renderInput={params => (
            <TextField
              {...params}
              label={t("energyReport.deviceFilter", "Fahrzeug, Kennzeichen oder Seriennummer")}
            />
          )}
        />
        <DateRangeSelect
          fromDate={fromDate}
          toDate={toDate}
          onChange={(from, to) => {
            if (from) setFromDate(from);
            if (to) setToDate(to);
          }}
        />
        {activeTab === "chargeEvents" && (
          <FormControlLabel
            control={
              <Switch
                checked={mergeSessions}
                onChange={event => setMergeSessions(event.target.checked)}
              />
            }
            label={t("energyReport.mergeSessions", "Ladevorgänge zusammenfassen")}
          />
        )}
      </Stack>

      <QualitySummary counts={counts} />

      {report.progress.isLoading && (
        <Box>
          <LinearProgress
            variant="determinate"
            value={
              report.progress.total > 0
                ? (report.progress.read / report.progress.total) * 100
                : 0
            }
          />
          <Typography variant="caption" color="text.secondary">
            {t("energyReport.loadProgress", "{{read}} von {{total}} Fahrzeugen gelesen", {
              read: report.progress.read,
              total: report.progress.total,
            })}
          </Typography>
        </Box>
      )}

      {report.progress.failed > 0 && (
        <Alert severity="warning">
          {t(
            "energyReport.failedRequests",
            "Für {{count}} Fahrzeuge konnte Geotab keine Daten liefern. Die betroffenen Zeilen sind in der Spalte Datenqualität markiert.",
            { count: report.progress.failed },
          )}
          {report.error ? ` (${report.error.message})` : ""}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_event, value: TabKey) => setActiveTab(value)}>
        <Tab value="consumption" label={t("energyReport.tabs.consumption", "Verbrauch")} />
        <Tab value="chargeEvents" label={t("energyReport.tabs.chargeEvents", "Ladevorgänge")} />
        <Tab value="fuelUpEvents" label={t("energyReport.tabs.fuelUpEvents", "Tankvorgänge")} />
      </Tabs>

      <Paper sx={{ flex: 1, minHeight: 420 }}>
        {activeTab === "consumption" && (
          <EnergyReportGrid
            rows={report.consumptionRows}
            columns={consumptionColumns}
            loading={allDevicesQuery.isPending}
            fileName={t("energyReport.file.consumption", "Energieverbrauch")}
            metadata={metadata}
            initialState={{
              columns: { columnVisibilityModel: CONSUMPTION_HIDDEN_COLUMNS },
              sorting: { sortModel: [{ field: "status", sort: "asc" }] },
            }}
          />
        )}
        {activeTab === "chargeEvents" && (
          <EnergyReportGrid
            rows={chargeEventRows}
            columns={chargeEventColumns}
            loading={report.progress.isLoading && chargeEventRows.length === 0}
            fileName={t("energyReport.file.chargeEvents", "Ladevorgaenge")}
            metadata={metadata}
            initialState={{ sorting: { sortModel: [{ field: "startTime", sort: "desc" }] } }}
          />
        )}
        {activeTab === "fuelUpEvents" && (
          <EnergyReportGrid
            rows={report.fuelUpEventRows}
            columns={fuelUpEventColumns}
            loading={report.progress.isLoading && report.fuelUpEventRows.length === 0}
            fileName={t("energyReport.file.fuelUpEvents", "Tankvorgaenge")}
            metadata={metadata}
            initialState={{ sorting: { sortModel: [{ field: "dateTime", sort: "desc" }] } }}
          />
        )}
      </Paper>
    </Stack>
  );
}

/** Make and model per vehicle, from the decoded VIN — shown on all three views. */
function useVinDecoded(devices: Device[]) {
  const vins = useMemo(
    () =>
      devices
        .map(device => (device as Device & { vehicleIdentificationNumber?: string })
          .vehicleIdentificationNumber)
        .filter((vin): vin is string => !!vin),
    [devices],
  );
  const { data: details } = useDecodeVins(vins);

  return useMemo(() => {
    const byVin = new Map((details ?? []).map(detail => [detail.vin, detail]));
    const byDeviceId = new Map<string, string>();

    for (const device of devices) {
      const vin = (device as Device & { vehicleIdentificationNumber?: string })
        .vehicleIdentificationNumber;
      const detail = vin ? byVin.get(vin) : undefined;
      const decoded = detail ? [detail.make, detail.model].filter(Boolean).join(" ") : "";
      if (decoded) {
        byDeviceId.set(device.id, decoded);
      }
    }

    return byDeviceId;
  }, [devices, details]);
}

function userLabel(me: { name?: string } | null | undefined, username?: string) {
  return me?.name?.trim() || username || "";
}
